// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Tabs } from '@mantine/core';
import type { Patient } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import type { PatientTabBadgeCounts } from '../../hooks/usePatientTabBadges';
import { PatientExportMenu } from './PatientExportMenu';
import type { PatientPageTabInfo } from './PatientPage.utils';
import classes from './PatientTabsNavigation.module.css';

interface PatientTabsNavigationProps {
  patient: Patient;
  tabs: PatientPageTabInfo[];
  currentTab: string;
  onTabChange: (value: string | null) => void;
  badges?: PatientTabBadgeCounts;
}

export function PatientTabsNavigation({
  patient,
  tabs,
  currentTab,
  onTabChange,
  badges,
}: PatientTabsNavigationProps): JSX.Element {
  return (
    <Tabs
      value={currentTab.toLowerCase()}
      onChange={onTabChange}
      variant="pills"
      radius="md"
      classNames={{ root: classes.nav }}
    >
      <div className={classes.row}>
        <Tabs.List className={classes.list}>
          {tabs.map((t) => {
            const count = badges?.[t.id];
            return (
              <Tabs.Tab
                key={t.id}
                value={t.id}
                leftSection={t.icon}
                rightSection={
                  count && count > 0 ? (
                    <Badge color="red" circle size="sm" aria-label={`${t.label} has ${count} item`}>
                      {count}
                    </Badge>
                  ) : undefined
                }
              >
                {t.label}
              </Tabs.Tab>
            );
          })}
        </Tabs.List>
        <div className={classes.actions}>
          <PatientExportMenu patient={patient} className={classes.exportButton} />
        </div>
      </div>
    </Tabs>
  );
}
